import { useMemo } from "react";
import { pickemsContest } from "../models/pickems";
import { useFirestore } from "../useFirestore";
import { usePickemsMarkets } from "./usePickemsMarkets";
import { parseDate } from "utilities/datetime";
import type { Always } from "types/utilities";

export const usePickemsContestWithMarkets = (contestId: string) => {
  const {
    contest,
    isLoading: contestLoading,
    error: contestError,
  } = usePickemsContest(contestId);

  const {
    markets,
    isLoading: marketsLoading,
    error: marketsError,
  } = usePickemsMarkets(contestId);

  const contestWithMarkets = useMemo(() => {
    if (!contest) return null;

    return {
      ...contest,
      markets,
    };
  }, [contest, markets]);

  return {
    contest: contestWithMarkets,
    isLoading: contestLoading || marketsLoading,
    error: contestError || marketsError,
  } as const;
};

export const usePickemsContest = (contestId: string) => {
  const { data, isLoading, error } = useFirestore(
    `/pickemsContests/${contestId}`,
    pickemsContest,
    {
      keyPath: "id",
    },
  );

  const transformedContest = useMemo(() => {
    if (!data) return null;

    return {
      ...data,
      scheduledStartTime: parseDate(data.scheduledStartTime),
    };
  }, [data]);

  return { contest: transformedContest, isLoading, error } as const;
};

export type PickemsContest = Always<
  ReturnType<typeof usePickemsContest>["contest"]
>;
export type PickemsContestWithMarkets = Always<
  ReturnType<typeof usePickemsContestWithMarkets>["contest"]
>;
